
import DeliveryOption from '@/controllers/options/DeliveryOption';
import { Prop, Vue, Watch } from 'vue-property-decorator';
import TestController from '@/controllers/TestController';
import PlayerEmitter from '../../event/PlayerEmitter';
import { inject } from 'vue';
import EventBusConstants from '@/constants/EventBusConstants';

export default class CountdownTimer extends Vue{
  @Prop({}) readonly timerDeliveryOption: DeliveryOption | undefined;
  @Prop({ default: false }) readonly pauseTest: boolean | undefined;
  private elapsedTime = 0;
  private timeLimit = 0;
  private warningTime = 0;
  private timerType = 'soft';

  private timerInterval: number | undefined;

  private testController: TestController | undefined = inject<TestController>('testController');
  private emitter: PlayerEmitter | undefined = inject('playerEmitter');

  @Watch('timerDeliveryOption')
  private deliveryOptionPropertyChanged(): void {
    this.reset();
  }

  @Watch('pauseTest')
  private pauseTestPropertyChanged(): void {
    this.manageTimer();
  }

  created() {
    this.reset();
  }

  mounted() {
    this.reset();
  }

  private get elapsedTimeConverted(): string {
    return this.convertTime(this.elapsedTime);
  }

  private get totalConverted(): string {
    return this.convertTime(this.timeLimit);
  }

  private reset(): void {
    this.stopTimer();
    this.manageWarningClass(true);

    const deliveryOption: DeliveryOption | undefined = this.timerDeliveryOption;

    this.elapsedTime = Number(this.getValue(deliveryOption, 'elapsedTime', '0'));
    this.timeLimit = Number(this.getValue(deliveryOption, 'time_limit', '0'));
    this.warningTime = this.timeLimit - Number(this.getValue(deliveryOption, 'warning_time', '0'));
    this.timerType = this.getValue(deliveryOption, 'time_limit_type', 'Soft');

    if (deliveryOption) {
      this.manageWarningClass();
      this.manageTimer();
    }
  }

  private getValue(deliveryOption: DeliveryOption | undefined, key: string, defaultValue: string): string {
    let value = defaultValue;
    if (deliveryOption) {
      let optionValue = deliveryOption.getSetting(key);
      if (optionValue) {
        value = optionValue;
      }
    }

    return value;
  }

  private isSoftTimer(): boolean {
    return this.timerType.toLowerCase() === 'soft';
  }

  private manageTimer(): void {
    if (this.pauseTest) {
      this.stopTimer();
    } else {
      this.startTimer();
    }
  }

  private startTimer(): void {
    if (!this.timerInterval) {
      this.initializeClock();
    }
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
  }

  private initializeClock(): void {
    if (this.elapsedTime >= 0) {

      if (this.elapsedTime >= this.timeLimit && !this.isSoftTimer()) {
        this.emitter!.emit(EventBusConstants.HARD_TIMER_EVENT, [["testExpired", true]]);
        return
      }

      this.timerInterval = window.setInterval(() => {
        this.elapsedTime = this.elapsedTime + 1;
        this.timerDeliveryOption?.addSetting('elapsedTime', `${this.elapsedTime}`);
        if (this.elapsedTime === this.warningTime) {
          this.manageWarningClass();
        }

        if (this.elapsedTime >= this.timeLimit && !this.isSoftTimer()) {
          this.stopTimer()
          this.emitter!.emit(EventBusConstants.HARD_TIMER_EVENT, [["testExpired", true]]);
        }
      }, 1000);
    }
  }

  private manageWarningClass(reset = false): void {
    const warningAnimation = 'warning-animation';
    const backgroundColor = 'warning-background';
    let element: Element | undefined = undefined;
    if (this.$el) {
      element = this.$el.querySelector('#countdown-information-container');
    }

    if (element) {
      if (reset) {
        this.removeClass(element, [warningAnimation, backgroundColor]);
      } else {
        if (this.elapsedTime === this.warningTime) {
          this.addClass(element, [warningAnimation, backgroundColor]);
        } else if (this.elapsedTime > this.warningTime) {
          this.addClass(element, [backgroundColor]);
          this.removeClass(element, [warningAnimation]);
        } else {
          this.removeClass(element, [warningAnimation, backgroundColor]);
        }
      }
    }
  }

  private addClass(element: Element, nameArray: Array<string>): void {
    nameArray.forEach((name) => {
      if (!element.classList.contains(name)) {
        element.classList.add(name);
      }
    });
  }

  private removeClass(element: Element, nameArray: Array<string>): void {
    nameArray.forEach((name) => {
      if (element.classList.contains(name)) {
        element.classList.remove(name);
      }
    });
  }

  private convertTime(time: number): string {
    const minutes = Math.floor(time! / 60);
    const seconds = time! - minutes * 60;
    return minutes + ':' + this.padValue(seconds, '0', 2);
  }

  private padValue(value: number, paddingValue: string, length: number): string {
    return (new Array(length + 1).join(paddingValue) + value).slice(-length);
  }
}
